import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { CopyIcon, ArrowForwardIcon } from '@rsos/assets/icons';
import { Box, Text, Button } from '@rsos/base-components';
import { ButtonText } from '@rsos/components/capstone/Location/styles/common.styles';
import {
  selectExistingRequest,
  AirAmbulanceRequest,
} from '@rsos/reduxDataStore';
import copyToClipboard from '@rsos/utils/copyToClipboard';
import {
  trackOpenTransportLink,
  trackCopyTrackingLink,
} from '../../../gainsightTrackings/airAmbulanceTrackings';

interface AircraftDetailsProps {
  brandTail: string;
}

interface TrackingUrlDisplayProps {
  trackingUrl: string;
}

interface TrackingActionsProps {
  trackingUrl?: string;
  onCopyLink: () => void;
  onTrackAirAmbulance: () => void;
}

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.primaryBase};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const AircraftDetails = ({ brandTail }: AircraftDetailsProps): JSX.Element => (
  <Box mt={2} mb={2}>
    <Text color="secondaryText" size="normal">
      Aircraft Name - Tail Number
    </Text>
    <Text color="primaryText" size="normal">
      {brandTail}
    </Text>
  </Box>
);

const TrackingUrlDisplay = ({
  trackingUrl,
}: TrackingUrlDisplayProps): JSX.Element => (
  <Box mt={2} mb={2}>
    <Text color="secondaryText" size="normal">
      Tracking URL
    </Text>
    <Text size="normal">
      <StyledLink href={trackingUrl} target="_blank" rel="noopener noreferrer">
        {trackingUrl}
      </StyledLink>
    </Text>
  </Box>
);

const TrackingActions = ({
  onCopyLink,
  onTrackAirAmbulance,
}: TrackingActionsProps): JSX.Element => (
  <Box horizontal justify="space-between" mt={2} mb={2}>
    <Button type="button" grow btnType="tertiary" onClick={onCopyLink}>
      <CopyIcon /> <ButtonText>Copy Link</ButtonText>
    </Button>
    <Button grow btnType="tertiary" type="button" onClick={onTrackAirAmbulance}>
      <ArrowForwardIcon /> <ButtonText>Track Air Ambulance</ButtonText>
    </Button>
  </Box>
);

const AirAmbulanceStatusTracking = (): JSX.Element | null => {
  const existingRequest: AirAmbulanceRequest | null = useSelector(
    selectExistingRequest,
  );
  const body = existingRequest?.body || {};
  const brandTail = body.brand_tail as string | undefined;
  const trackingUrl = body.tracking_url as string | undefined;
  const extTripId = body.ext_trip_id as string | undefined;
  const requestId =
    (existingRequest?.request_id as string | undefined) || undefined;

  const handleCopyLink = () => {
    if (trackingUrl) {
      copyToClipboard(trackingUrl);
    }
    trackCopyTrackingLink({
      airAmbulanceUrl: trackingUrl,
      requestId: extTripId || requestId,
    });
  };

  const handleTrackAirAmbulance = () => {
    window.open(trackingUrl, '_blank', 'noopener,noreferrer');
    trackOpenTransportLink({
      airAmbulanceUrl: trackingUrl,
      requestId: extTripId || requestId,
    });
  };

  // Only render if we have actual tracking data to display
  const hasTrackingData = brandTail || trackingUrl;

  if (!hasTrackingData) {
    return null;
  }

  return (
    <>
      <Box m={1}>
        {brandTail && <AircraftDetails brandTail={brandTail} />}
        {trackingUrl && <TrackingUrlDisplay trackingUrl={trackingUrl} />}
      </Box>
      {trackingUrl && (
        <TrackingActions
          trackingUrl={trackingUrl}
          onCopyLink={handleCopyLink}
          onTrackAirAmbulance={handleTrackAirAmbulance}
        />
      )}
    </>
  );
};

export default AirAmbulanceStatusTracking;
